/**
 * POLISH PHOTO GALLERY
 *
 * Renders aggregated swatch / nail-art / macro photos contributed by
 * collectors with public profiles. Each photo shows the contributor's
 * name and links to their public collection.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { PolishContribution } from '../../services/polishCatalogService';

interface Props {
  contributions: PolishContribution[];
  initialLimit?: number;
}

export default function PolishPhotoGallery({ contributions, initialLimit = 60 }: Props) {
  const [showAll, setShowAll] = useState(false);

  const flat = contributions.flatMap((c) =>
    c.photos.map((photo) => ({ ...photo, contributor: c }))
  );

  if (flat.length === 0) {
    return (
      <div className="bg-slate-800/40 border border-slate-600 rounded-lg p-8 text-center">
        <p className="text-glass-secondary">
          No collector photos for this polish yet.
        </p>
        <p className="text-sm text-glass-muted mt-2">
          Be the first to add a swatch — collectors with public profiles
          will see their photos here.
        </p>
      </div>
    );
  }

  const visible = showAll ? flat : flat.slice(0, initialLimit);

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {visible.map((photo, i) => {
          const c = photo.contributor;
          const profileHref = c.username ? `/profile/${c.username}/collection` : undefined;
          return (
            <div
              key={`${c.userId}-${i}`}
              className="bg-slate-800/40 border border-slate-600 rounded-lg overflow-hidden"
            >
              <div className="relative aspect-square bg-slate-900">
                <img
                  src={photo.url}
                  alt={photo.caption || `Photo by ${c.displayName ?? c.username ?? 'collector'}`}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                {photo.type && photo.type !== 'other' && (
                  <span className="absolute top-1 left-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wide bg-black/60 text-white rounded">
                    {photo.type}
                  </span>
                )}
              </div>
              {profileHref ? (
                <Link
                  to={profileHref}
                  className="block px-2 py-1.5 text-xs text-glass-muted hover:text-glass hover:bg-slate-700/40 truncate"
                >
                  📷 {c.displayName ?? `@${c.username}`}
                </Link>
              ) : (
                <div className="px-2 py-1.5 text-xs text-glass-muted truncate">
                  📷 {c.displayName ?? 'Anonymous'}
                </div>
              )}
              {/* Optional per-photo context — skin tone and/or lighting.
                  Joined by "·" when both are present. Absent line when
                  neither field is filled in. */}
              {(photo.skinTone || photo.lighting) && (
                <div
                  className="px-2 pb-1.5 text-[10px] text-glass-muted truncate"
                  title={[photo.skinTone, photo.lighting].filter(Boolean).join(' · ')}
                >
                  {[photo.skinTone, photo.lighting].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!showAll && flat.length > initialLimit && (
        <div className="mt-4 text-center">
          <button
            onClick={() => setShowAll(true)}
            className="px-4 py-2 text-sm bg-purple-600/30 text-purple-300 rounded hover:bg-purple-600/50 transition-colors"
          >
            Show all {flat.length} photos
          </button>
        </div>
      )}
    </div>
  );
}